import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Shield,
  Send,
  Loader2,
  Lightbulb,
  AlertTriangle,
  MessageSquare,
  Clock,
  ChevronRight,
  Trash2,
  Copy,
  Check,
  HelpCircle,
  Zap,
  Target,
  History,
  Mic,
} from "lucide-react";

export default function ObjectionHandler() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [objection, setObjection] = useState("");
  const [context, setContext] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const res = await fetch(`${API}/objection-handler/history`, { credentials: "include" });
      if (res.ok) setHistory(await res.json());
    } catch (err) {
      console.error("Failed to fetch history:", err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!objection.trim()) {
      toast.error("Please enter an objection");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/objection-handler/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ objection: objection.trim(), context: context.trim() }),
      });
      if (!res.ok) throw new Error("Failed to analyze objection");
      const data = await res.json();
      setResult(data.analysis);
      setHistory(prev => [{ entry_id: data.entry_id, objection, context, analysis: data.analysis, created_at: new Date().toISOString() }, ...prev]);
      toast.success("Analysis complete!");
    } catch (err) {
      toast.error("Failed to analyze objection", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleDelete = async () => {
    if (!deleteDialog.id) return;
    try {
      const res = await fetch(`${API}/objection-handler/${deleteDialog.id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        setHistory(prev => prev.filter(h => h.entry_id !== deleteDialog.id));
        toast.success("Entry deleted");
      }
    } catch (err) {
      toast.error("Failed to delete");
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };

  const handleLoadFromHistory = (entry) => {
    setObjection(entry.objection);
    setContext(entry.context || "");
    setResult(entry.analysis);
    setShowHistory(false);
  };

  const severityColor = (severity) => {
    if (severity === "low") return "bg-green-500/10 text-green-500";
    if (severity === "medium") return "bg-yellow-500/10 text-yellow-500";
    return "bg-red-500/10 text-red-500";
  };

  const typeColor = (type) => {
    const colors = {
      price: "bg-emerald-500/10 text-emerald-500",
      timing: "bg-blue-500/10 text-blue-500",
      competition: "bg-orange-500/10 text-orange-500",
      need: "bg-purple-500/10 text-purple-500",
      authority: "bg-pink-500/10 text-pink-500",
      trust: "bg-yellow-500/10 text-yellow-500",
    };
    return colors[type] || "bg-muted text-muted-foreground";
  };

  return (
    <div className="max-w-4xl mx-auto space-y-8" data-testid="objection-handler-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold">Objection Handler</h1>
          <p className="text-muted-foreground mt-1">Get AI-powered strategies to overcome any sales objection</p>
        </div>
        <Button
          variant="outline"
          onClick={() => setShowHistory(!showHistory)}
          data-testid="toggle-history-btn"
        >
          <History className="w-4 h-4 mr-2" />
          {showHistory ? "New Analysis" : "History"}
          {!showHistory && history.length > 0 && (
            <Badge variant="secondary" className="ml-2 text-xs">{history.length}</Badge>
          )}
        </Button>
      </div>

      {showHistory ? (
        /* History View */
        <Card className="border-border" data-testid="history-panel">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-primary" />
              Past Analyses
            </CardTitle>
          </CardHeader>
          <CardContent>
            {historyLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : history.length > 0 ? (
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {history.map((entry) => (
                    <div
                      key={entry.entry_id}
                      className="flex items-center justify-between p-4 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer group"
                      onClick={() => handleLoadFromHistory(entry)}
                      data-testid={`history-entry-${entry.entry_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">"{entry.objection}"</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`text-[10px] px-1.5 py-0 ${typeColor(entry.analysis?.objection_type)}`}>
                            {entry.analysis?.objection_type}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {new Date(entry.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
                          onClick={(e) => { e.stopPropagation(); setDeleteDialog({ open: true, id: entry.entry_id }); }}
                          data-testid={`delete-history-${entry.entry_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            ) : (
              <div className="text-center py-12">
                <Shield className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground text-sm">No analyses yet. Submit your first objection above!</p>
              </div>
            )}
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Input Section */}
          <Card className="border-border" data-testid="objection-input-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Shield className="w-5 h-5 text-primary" />
                What objection are you facing?
              </CardTitle>
              <CardDescription>
                Enter the exact objection a prospect gave you, and get proven strategies to handle it.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="objection">The Objection</Label>
                <Textarea
                  id="objection"
                  placeholder='e.g., "Your price is too high" or "We already have a vendor for that" or "I need to think about it"'
                  value={objection}
                  onChange={(e) => setObjection(e.target.value)}
                  rows={3}
                  className="resize-none"
                  data-testid="objection-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="context" className="text-muted-foreground text-sm">Additional Context (optional)</Label>
                <Input
                  id="context"
                  placeholder="e.g., SaaS product, mid-market prospect, second call"
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  data-testid="context-input"
                />
              </div>
              <Button
                onClick={handleSubmit}
                disabled={loading || !objection.trim()}
                className="w-full bg-primary hover:bg-primary/90"
                size="lg"
                data-testid="analyze-objection-btn"
              >
                {loading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Analyzing...</>
                ) : (
                  <><Send className="w-4 h-4 mr-2" /> Get Strategies</>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Results Section */}
          {result && (
            <div className="space-y-6" data-testid="objection-results">
              {/* Overview */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <Card className="border-border">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${typeColor(result.objection_type)}`}>
                      <Target className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Type</p>
                      <p className="font-medium capitalize" data-testid="objection-type">{result.objection_type}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${severityColor(result.severity)}`}>
                      <AlertTriangle className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Difficulty</p>
                      <p className="font-medium capitalize" data-testid="objection-severity">{result.severity}</p>
                    </div>
                  </CardContent>
                </Card>
                <Card className="border-border">
                  <CardContent className="p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <HelpCircle className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Why They Say This</p>
                      <p className="text-sm text-muted-foreground line-clamp-2">{result.why_they_say_this}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Recommended Responses */}
              <Card className="border-border" data-testid="recommended-responses-card">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-primary" />
                    Recommended Responses
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {result.recommended_responses?.map((resp, idx) => (
                    <div key={idx} className="rounded-lg border border-border p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="font-medium">
                          {resp.approach}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-muted-foreground"
                          onClick={() => handleCopy(resp.response, idx)}
                          data-testid={`copy-response-${idx}`}
                        >
                          {copiedIdx === idx ? (
                            <><Check className="w-3 h-3 mr-1" /> Copied</>
                          ) : (
                            <><Copy className="w-3 h-3 mr-1" /> Copy</>
                          )}
                        </Button>
                      </div>
                      <p className="text-sm leading-relaxed bg-muted/50 rounded-lg p-3 italic" data-testid={`response-text-${idx}`}>
                        "{resp.response}"
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Lightbulb className="w-3 h-3" /> {resp.why_it_works}
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Things to Avoid & Follow-ups */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="border-border" data-testid="things-to-avoid-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      Things to Avoid
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {result.things_to_avoid?.map((item, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-red-500 mt-2 shrink-0" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>

                <Card className="border-border" data-testid="follow-up-questions-card">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-blue-500" />
                      Follow-Up Questions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {result.follow_up_questions?.map((q, idx) => (
                        <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-2 shrink-0" />
                          "{q}"
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </div>

              {/* Pro Tip */}
              {result.pro_tip && (
                <Card className="border-primary/30 bg-primary/5" data-testid="pro-tip-card">
                  <CardContent className="p-4 flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <Zap className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Pro Tip</p>
                      <p className="text-sm text-muted-foreground mt-1">{result.pro_tip}</p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Practice This Objection CTA */}
              <Card className="border-border bg-muted/30" data-testid="practice-objection-card">
                <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div>
                    <p className="font-heading font-semibold text-lg">Ready to practice?</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Jump into a live roleplay scenario pre-loaded with this objection.
                    </p>
                  </div>
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 shadow-lg shrink-0"
                    onClick={() => {
                      const truncated = objection.length > 60 ? objection.slice(0, 57) + "..." : objection;
                      navigate("/roleplay/new", {
                        state: {
                          scenarioSettings: {
                            name: `Objection Practice: ${truncated}`,
                            scenario_type: "objection_handling",
                            client_personality: result.severity === "high" ? "skeptical" : "analytical",
                            industry: "tech",
                            company_size: "smb",
                            specific_objections: [objection],
                            goals: ["Successfully overcome the objection", "Keep the conversation moving forward"],
                            context: context || `The prospect raised this objection: "${objection}". Practice handling it effectively.`,
                            voice: "shimmer",
                            difficulty: result.severity === "high" ? "hard" : result.severity === "low" ? "easy" : "medium",
                          },
                        },
                      });
                    }}
                    data-testid="practice-objection-btn"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    Practice This Objection
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Entry?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this objection analysis. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <Trash2 className="w-4 h-4 mr-2" /> Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
